/** Social view-models for the stage: situation, fans vs model, challenge text, presence, reaction anchors. Pure. */
import { formatClock, type CallLock, type Choice, type GameEvent, type RoomSnapshot, type Side } from '@clutch/core';
import { chanceView, choiceLabel, choiceSide, firstName, sideName } from '../room/derive';

type Snap = Pick<RoomSnapshot, 'meta' | 'state' | 'call' | 'locks' | 'probability' | 'presence'>;

/** "3:44 left in Q4" / "0:42 left in OT". */
export function timeLeft(period: number, clockSec: number): string {
  const [label, clock] = formatClock(period, clockSec).split(' ');
  return `${clock} left in ${label}`;
}

/**
 * The one line a stranger needs: "HAWKS DOWN 3 · 3:44 LEFT IN Q4".
 * Told from the call's subject when there is a call, else from whoever trails.
 */
export function situationLine(s: Pick<RoomSnapshot, 'meta' | 'state' | 'call'>): string {
  const st = s.state;
  if (st.status === 'pre') return 'Tip-off soon';
  const diff = st.homeScore - st.awayScore;
  if (st.status === 'final') {
    if (diff === 0) return 'Final';
    const w: Side = diff > 0 ? 'home' : 'away';
    return `Final · ${sideName(s, w)} win by ${Math.abs(diff)}`;
  }
  if (st.period === 2 && st.clockSec === 0) {
    return diff === 0 ? 'Halftime · all tied up' : `Halftime · ${sideName(s, diff > 0 ? 'home' : 'away')} lead by ${Math.abs(diff)}`;
  }
  const left = timeLeft(st.period, st.clockSec);
  if (diff === 0) return `Tied · ${left}`;
  const lead: Side = diff > 0 ? 'home' : 'away';
  const subject = s.call?.subject ?? (lead === 'home' ? 'away' : 'home');
  const n = Math.abs(diff);
  return subject === lead ? `${sideName(s, subject)} up ${n} · ${left}` : `${sideName(s, subject)} down ${n} · ${left}`;
}

export type FansVsModel = {
  room: string; // "Room: 1 Hawks · 1 Knicks" or "Room: 67% Hawks (3 calls)"
  model?: string; // "Model 20% Hawks"
  yes: number;
  no: number;
};

/** Confirmed locks vs the latest model number. No room percentages under 3 calls. */
export function fansVsModel(s: Snap): FansVsModel | undefined {
  const call = s.call;
  if (!call) return undefined;
  const locks = s.locks.filter((l) => l.callId === call.callId && l.status === 'confirmed');
  const yes = locks.filter((l) => l.choice === 'yes').length;
  const no = locks.length - yes;
  let room: string;
  if (locks.length === 0) room = 'Room: no calls yet';
  else if (locks.length >= 3) {
    const lead: Choice = yes >= no ? 'yes' : 'no';
    const share = Math.round(((lead === 'yes' ? yes : no) / locks.length) * 100);
    room = `Room: ${share}% ${choiceLabel(call, lead)} (${locks.length} calls)`;
  } else {
    const parts: string[] = [];
    if (yes) parts.push(`${yes} ${call.yesLabel}`);
    if (no) parts.push(`${no} ${call.noLabel}`);
    room = `Room: ${parts.join(' · ')}`;
  }
  const v = chanceView(s);
  const model = v.status === 'ready' ? `Model ${v.pct}% ${sideName(s, v.side)}` : undefined;
  return { room, model, yes, no };
}

/** Share text built only from real state: "Hawks down 3 vs the Knicks, 3:44 left in Q4. I'm calling it — you?" */
export function challengeText(s: Pick<RoomSnapshot, 'meta' | 'state' | 'call'>, mine?: Pick<CallLock, 'choice'>): string {
  const st = s.state;
  const diff = st.homeScore - st.awayScore;
  const subject: Side = s.call?.subject ?? (diff > 0 ? 'away' : 'home');
  const opp: Side = subject === 'home' ? 'away' : 'home';
  const sMargin = subject === 'home' ? diff : -diff;
  const left = timeLeft(st.period, st.clockSec);
  const situation =
    st.status === 'final'
      ? `Final: ${sideName(s, 'away')} ${st.awayScore}, ${sideName(s, 'home')} ${st.homeScore}.`
      : sMargin === 0
        ? `${sideName(s, subject)} and ${sideName(s, opp)} tied, ${left}.`
        : `${sideName(s, subject)} ${sMargin > 0 ? 'up' : 'down'} ${Math.abs(sMargin)} vs the ${sideName(s, opp)}, ${left}.`;
  if (mine && s.call) return `${situation} I called ${choiceLabel(s.call, mine.choice)} — you?`;
  return `${situation} I'm calling it — you?`;
}

export type FriendChip = {
  fanId: string;
  name: string; // first name ("Malik (bot)" keeps its label)
  initials: string;
  me: boolean;
  callSide?: Side; // the side their confirmed call backs
  callLabel?: string; // "Knicks"
};

/** Who's here, with their confirmed calls — the people the stage puts on top. */
export function friendChips(s: Snap, myFanId: string, initialsOf: (n: string) => string): FriendChip[] {
  const call = s.call;
  const online = s.presence.filter((p) => p.online);
  const chips = online.map((p): FriendChip => {
    const lock = call ? s.locks.find((l) => l.callId === call.callId && l.fanId === p.fanId && l.status === 'confirmed') : undefined;
    return {
      fanId: p.fanId,
      name: firstName(p.displayName),
      initials: initialsOf(p.displayName),
      me: p.fanId === myFanId,
      ...(lock && call ? { callSide: choiceSide(call, lock.choice), callLabel: choiceLabel(call, lock.choice) } : {}),
    };
  });
  // Friends first (callers before lurkers), me last.
  return chips.sort((a, b) => Number(a.me) - Number(b.me) || Number(!a.callLabel) - Number(!b.callLabel));
}

// ───────────── Reactions anchored to plays ─────────────

export type AnchoredReaction = {
  id: number;
  fanId: string;
  name: string;
  emoji: string;
  atSeq: number;
  playEventId?: string;
  mine: boolean;
};

export type ReactionStack = { emoji: string; count: number; mine: boolean; names: string[] };

/**
 * Group reactions under the play they answered: `playEventId` when the server sent one,
 * else the latest caption-worthy play at or before `atSeq`.
 */
export function anchorReactions(
  reactions: readonly AnchoredReaction[],
  plays: readonly Pick<GameEvent, 'eventId' | 'seq'>[],
): Map<string, ReactionStack[]> {
  const known = new Set(plays.map((p) => p.eventId));
  const out = new Map<string, ReactionStack[]>();
  for (const r of reactions) {
    let id = r.playEventId && known.has(r.playEventId) ? r.playEventId : undefined;
    if (!id) {
      for (let i = plays.length - 1; i >= 0; i--) {
        if (plays[i]!.seq <= r.atSeq) {
          id = plays[i]!.eventId;
          break;
        }
      }
    }
    if (!id) id = r.playEventId;
    if (!id) continue;
    const stacks = out.get(id) ?? [];
    const st = stacks.find((x) => x.emoji === r.emoji);
    if (st) {
      st.count++;
      st.mine ||= r.mine;
      if (!st.names.includes(r.name)) st.names.push(r.name);
    } else stacks.push({ emoji: r.emoji, count: 1, mine: r.mine, names: [r.name] });
    out.set(id, stacks);
  }
  return out;
}
